/**
 * PaymentPlanModal - Shows loan installments remaining and recurring payment yearly costs
 */

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Landmark,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  Calendar,
} from 'lucide-react';
import { useCashflowStore } from '../../stores/cashflowStore';
import { cashflow } from '../../services/supabaseService';

// Format currency
const formatCurrency = (amount, currency = 'TRY') => {
  const formatter = new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return formatter.format(amount);
};

// Calculate yearly cost based on frequency
const calculateYearlyCost = (amount, frequency) => {
  switch (frequency) {
    case 'weekly':
      return amount * 52;
    case 'biweekly':
      return amount * 26;
    case 'monthly':
      return amount * 12;
    case 'yearly':
      return amount;
    default:
      return amount * 12; // Default to monthly
  }
};

// Frequency labels
const FREQUENCY_LABELS = {
  weekly: 'Haftalık',
  biweekly: '2 Haftalık',
  monthly: 'Aylık',
  yearly: 'Yıllık',
};

const PaymentPlanModal = ({ isOpen, onClose }) => {
  const { recurringParents } = useCashflowStore();
  const [loanGroups, setLoanGroups] = useState([]);
  const [isLoadingLoans, setIsLoadingLoans] = useState(true);
  const [activeTab, setActiveTab] = useState('loans'); // 'loans' | 'recurring'

  // Load loan groups with remaining installments
  useEffect(() => {
    const loadLoanGroups = async () => {
      if (!isOpen) return;

      setIsLoadingLoans(true);
      try {
        const groups = await cashflow.getLoanGroups();
        setLoanGroups(groups || []);
      } catch (error) {
        console.error('Error loading loan groups:', error);
      }
      setIsLoadingLoans(false);
    };

    loadLoanGroups();
  }, [isOpen]);

  if (!isOpen) return null;

  // Calculate loan totals
  const loanTotals = loanGroups.reduce((acc, group) => {
    const remaining = group.installments.filter(i => i.status !== 'completed');
    const remainingAmount = remaining.reduce((sum, i) => sum + parseFloat(i.amount), 0);
    acc.totalRemaining += remainingAmount;
    acc.totalInstallments += remaining.length;
    return acc;
  }, { totalRemaining: 0, totalInstallments: 0 });

  // Get recurring expenses (non-loan)
  const recurringExpenses = recurringParents.filter(
    e => e.type === 'expense' && !e.loan_group_id && e.is_recurring
  );

  // Calculate yearly totals for recurring
  const recurringYearlyTotal = recurringExpenses.reduce((sum, entry) => {
    const freq = entry.recurring_rule?.frequency || 'monthly';
    return sum + calculateYearlyCost(parseFloat(entry.amount), freq);
  }, 0);

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-gray-900 border border-gray-800 rounded-xl shadow-2xl max-h-[85vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-500/20 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Ödeme Planı</h2>
              <p className="text-sm text-gray-400">Kredi taksitleri ve tekrarlayan ödemeler</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Buttons - Always visible */}
        <div className="flex border-b border-gray-800">
          <button
            onClick={() => setActiveTab('loans')}
            className={`flex-1 px-4 py-3 flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'loans'
                ? 'bg-blue-500/10 text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
            }`}
          >
            <Landmark className="w-4 h-4" />
            <span className="font-medium">Kredi Taksitleri</span>
            <span className="text-xs opacity-70">({loanGroups.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('recurring')}
            className={`flex-1 px-4 py-3 flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'recurring'
                ? 'bg-purple-500/10 text-purple-400 border-b-2 border-purple-400'
                : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
            }`}
          >
            <RotateCcw className="w-4 h-4" />
            <span className="font-medium">Tekrarlayan</span>
            <span className="text-xs opacity-70">({recurringExpenses.length})</span>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Loan Installments Tab */}
          {activeTab === 'loans' && (
            <div className="p-4">
              {isLoadingLoans ? (
                <div className="py-8 text-center text-gray-500">
                  Yükleniyor...
                </div>
              ) : loanGroups.length === 0 ? (
                <div className="py-8 text-center text-gray-500">
                  Kredi taksiti bulunamadı
                </div>
              ) : (
                <>
                  {/* Loan list */}
                  <div className="space-y-3">
                    {loanGroups.map((group) => {
                      const remaining = group.installments.filter(i => i.status !== 'completed');
                      const completed = group.installments.filter(i => i.status === 'completed');
                      const remainingAmount = remaining.reduce((sum, i) => sum + parseFloat(i.amount), 0);
                      const firstInstallment = group.installments[0];
                      const currency = firstInstallment?.currency || 'TRY';

                      return (
                        <div
                          key={group.id}
                          className="bg-gray-800/50 rounded-lg p-3"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <p className="font-medium text-white">
                                {firstInstallment?.title || 'Kredi'}
                              </p>
                              <p className="text-xs text-gray-500">
                                {firstInstallment?.account_name || 'Hesap belirtilmedi'}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-medium text-red-400">
                                {formatCurrency(remainingAmount, currency)}
                              </p>
                              <p className="text-xs text-gray-500">
                                kalan tutar
                              </p>
                            </div>
                          </div>

                          {/* Progress bar */}
                          <div className="mb-2">
                            <div className="flex justify-between text-xs text-gray-400 mb-1">
                              <span>{completed.length} ödendi</span>
                              <span>{remaining.length} kaldı</span>
                            </div>
                            <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-emerald-500 rounded-full transition-all"
                                style={{
                                  width: `${(completed.length / group.installments.length) * 100}%`,
                                }}
                              />
                            </div>
                          </div>

                          <div className="text-xs text-gray-500">
                            Taksit: {formatCurrency(parseFloat(firstInstallment?.amount || 0), currency)} / ay
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Loan total */}
                  <div className="mt-4 pt-4 border-t border-gray-700">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-300">
                        Toplam Kalan ({loanTotals.totalInstallments} taksit)
                      </span>
                      <span className="text-xl font-bold text-red-400">
                        {formatCurrency(loanTotals.totalRemaining)}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Recurring Payments Tab */}
          {activeTab === 'recurring' && (
            <div className="p-4">
              {recurringExpenses.length === 0 ? (
                <div className="py-8 text-center text-gray-500">
                  Tekrarlayan ödeme bulunamadı
                </div>
              ) : (
                <>
                  {/* Table header */}
                  <div className="grid grid-cols-12 gap-2 px-3 py-2 text-xs text-gray-500 border-b border-gray-800">
                    <div className="col-span-5">Ödeme</div>
                    <div className="col-span-2 text-right">Tutar</div>
                    <div className="col-span-2 text-center">Sıklık</div>
                    <div className="col-span-3 text-right">Yıllık</div>
                  </div>

                  {/* Recurring list */}
                  <div className="divide-y divide-gray-800">
                    {recurringExpenses.map((entry) => {
                      const freq = entry.recurring_rule?.frequency || 'monthly';
                      const yearlyCost = calculateYearlyCost(parseFloat(entry.amount), freq);
                      const currency = entry.currency || 'TRY';

                      return (
                        <div
                          key={entry.id}
                          className="grid grid-cols-12 gap-2 px-3 py-3 items-center hover:bg-gray-800/30"
                        >
                          <div className="col-span-5">
                            <p className="text-sm text-white truncate">{entry.title}</p>
                            <p className="text-xs text-gray-500">{entry.category}</p>
                          </div>
                          <div className="col-span-2 text-right">
                            <span className="text-sm text-gray-300">
                              {formatCurrency(parseFloat(entry.amount), currency)}
                            </span>
                          </div>
                          <div className="col-span-2 text-center">
                            <span className="text-xs text-gray-400 bg-gray-800 px-2 py-1 rounded">
                              {FREQUENCY_LABELS[freq] || freq}
                            </span>
                          </div>
                          <div className="col-span-3 text-right">
                            <span className="text-sm font-medium text-purple-400">
                              {formatCurrency(yearlyCost, currency)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Recurring total */}
                  <div className="mt-4 pt-4 border-t border-gray-700">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-300">Toplam Yıllık Maliyet</span>
                      <span className="text-xl font-bold text-purple-400">
                        {formatCurrency(recurringYearlyTotal)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Aylık ortalama: {formatCurrency(recurringYearlyTotal / 12)}
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-800">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors"
          >
            Kapat
          </button>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
};

export default PaymentPlanModal;
